import type { MeasurementStrategyContext, PdpMeasurementStrategy } from './PdpMeasurementStrategy';

const VARIANT_OPTION_SELECTOR =
  '[data-variant-switch], .product-detail-configurator-option, .product-detail-configurator-option-label';

const CONFIGURATOR_ROOT_SELECTOR = '.product-detail-configurator, fieldset.product-detail-configurator-group';

function isVariantInteractionTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el?.closest) {
    return false;
  }

  if (!el.closest(CONFIGURATOR_ROOT_SELECTOR)) {
    return false;
  }

  if (el.closest(VARIANT_OPTION_SELECTOR)) {
    return true;
  }

  if (el instanceof HTMLInputElement && el.type === 'radio') {
    return true;
  }

  return false;
}

/**
 * PDP variant / configurator engagement: counts intent inside the configurator only, using an early
 * pointer phase so full-page variant navigations still emit `variant_clicks` before the browser tears
 * down the document (a plain `click` listener is too late for many Shopware PDP setups).
 *
 * Plugin coverage: yes — same DOM patterns as core PDP, which many variant UX extensions keep (e.g.
 * Studio Solid “Advanced Variant Display Types”, step-by-step configurator extensions, DizTech “Show /
 * Hide Unavailable Variants”, ZweiPunkt grey-out variants; Shape-and-Shift `shopware-variant-switch` on
 * listing/cart still reflects configurator-style markup where applicable). Extend selectors if a plugin
 * moves options outside `.product-detail-configurator`.
 */
export class VariantInteractionStrategy implements PdpMeasurementStrategy {
  private ctx: MeasurementStrategyContext | null = null;

  private onPointerDown: ((ev: Event) => void) | null = null;

  private onMouseDown: ((ev: Event) => void) | null = null;

  attach(ctx: MeasurementStrategyContext): void {
    this.ctx = ctx;
    const { events, store, document: doc } = ctx;

    const bump = (ev: Event) => {
      if (!isVariantInteractionTarget(ev.target)) {
        return;
      }
      store.incrementVariantClick();
    };

    this.onPointerDown = bump;
    events.on(doc, 'pointerdown', this.onPointerDown, { capture: true });

    if (typeof PointerEvent === 'undefined') {
      this.onMouseDown = bump;
      events.on(doc, 'mousedown', this.onMouseDown, { capture: true });
    }
  }

  dispose(): void {
    if (this.ctx != null) {
      if (this.onPointerDown != null) {
        this.ctx.events.off(this.ctx.document, 'pointerdown', this.onPointerDown, { capture: true });
      }
      if (this.onMouseDown != null) {
        this.ctx.events.off(this.ctx.document, 'mousedown', this.onMouseDown, { capture: true });
      }
    }
    this.onPointerDown = null;
    this.onMouseDown = null;
    this.ctx = null;
  }
}
